/**
 * Roles System Data for Frontend
 *
 * This file contains all role information in a format ready for frontend use.
 * Import this data to build the roles documentation page.
 */

export interface Role {
  id: "superadmin" | "owner" | "admin" | "member";
  name: string;
  icon: string;
  color: string;
  level: number;
  description: string;
  capabilities: string[];
  limitations: string[];
  permissions: Permission[];
}

export interface Permission {
  id: string;
  name: string;
  icon: string;
  description: string;
  allowed: boolean;
}

export interface InvitationRule {
  inviterRole: "superadmin" | "owner" | "admin" | "member";
  canInvite: string[];
  cannotInvite: string[];
  explanation: string;
}

export interface JourneyStep {
  step: number;
  title: string;
  description: string;
  outcome: string;
  role: string;
}

// ============================================
// ROLES DATA
// ============================================

export const ROLES: Role[] = [
  {
    id: "superadmin",
    name: "Super Admin",
    icon: "👑",
    color: "#FFD700",
    level: 4,
    description:
      "Platform-level administrator with access to all organizations",
    capabilities: [
      "Access to ALL organizations on the platform",
      "Full control over any organization",
      "All agent permissions (install, configure, enable, disable, pause, resume, uninstall)",
      "Platform-level administrative operations",
      "Manage billing and subscriptions",
      "Add and remove team members",
      "Change member roles",
      "Transfer ownership of any organization",
      "Delete any organization",
    ],
    limitations: [],
    permissions: [
      {
        id: "view",
        name: "View Agents",
        icon: "👁️",
        description: "Can see agents and data",
        allowed: true,
      },
      {
        id: "configure",
        name: "Configure Settings",
        icon: "⚙️",
        description: "Can change agent settings",
        allowed: true,
      },
      {
        id: "enable",
        name: "Enable Agents",
        icon: "▶️",
        description: "Can activate agents",
        allowed: true,
      },
      {
        id: "disable",
        name: "Disable Agents",
        icon: "⏸️",
        description: "Can deactivate agents",
        allowed: true,
      },
      {
        id: "pause",
        name: "Pause Agents",
        icon: "⏸️",
        description: "Can pause agent operations",
        allowed: true,
      },
      {
        id: "resume",
        name: "Resume Agents",
        icon: "▶️",
        description: "Can resume paused agents",
        allowed: true,
      },
      {
        id: "uninstall",
        name: "Uninstall Agents",
        icon: "🗑️",
        description: "Can remove agents from organization",
        allowed: true,
      },
      {
        id: "billing",
        name: "Manage Billing",
        icon: "💳",
        description: "Can manage subscriptions and payments",
        allowed: true,
      },
      {
        id: "admin",
        name: "Admin Operations",
        icon: "🔧",
        description: "Can perform administrative operations",
        allowed: true,
      },
      {
        id: "add_members",
        name: "Add Team Members",
        icon: "➕",
        description: "Can invite and add team members",
        allowed: true,
      },
      {
        id: "remove_members",
        name: "Remove Team Members",
        icon: "➖",
        description: "Can remove team members",
        allowed: true,
      },
      {
        id: "change_roles",
        name: "Change Member Roles",
        icon: "🔄",
        description: "Can change member roles",
        allowed: true,
      },
      {
        id: "transfer_ownership",
        name: "Transfer Ownership",
        icon: "↔️",
        description: "Can transfer organization ownership",
        allowed: true,
      },
      {
        id: "delete_org",
        name: "Delete Organization",
        icon: "🗑️",
        description: "Can delete the organization",
        allowed: true,
      },
      {
        id: "access_all_orgs",
        name: "Access All Organizations",
        icon: "🌐",
        description: "Can access all organizations on the platform",
        allowed: true,
      },
    ],
  },
  {
    id: "owner",
    name: "Owner",
    icon: "🏢",
    color: "#4A90E2",
    level: 3,
    description: "Organization owner with full control over their organization",
    capabilities: [
      "Full control of their organization",
      "Install and configure agents",
      "Manage billing and subscriptions",
      "Add and remove team members",
      "Change member roles (promote/demote)",
      "Transfer ownership to another user",
      "Delete the organization",
      "Uninstall agents",
      "All agent permissions",
    ],
    limitations: [
      "Cannot access other organizations (unless also a member)",
      "Cannot assign Super Admin role",
    ],
    permissions: [
      {
        id: "view",
        name: "View Agents",
        icon: "👁️",
        description: "Can see agents and data",
        allowed: true,
      },
      {
        id: "configure",
        name: "Configure Settings",
        icon: "⚙️",
        description: "Can change agent settings",
        allowed: true,
      },
      {
        id: "enable",
        name: "Enable Agents",
        icon: "▶️",
        description: "Can activate agents",
        allowed: true,
      },
      {
        id: "disable",
        name: "Disable Agents",
        icon: "⏸️",
        description: "Can deactivate agents",
        allowed: true,
      },
      {
        id: "pause",
        name: "Pause Agents",
        icon: "⏸️",
        description: "Can pause agent operations",
        allowed: true,
      },
      {
        id: "resume",
        name: "Resume Agents",
        icon: "▶️",
        description: "Can resume paused agents",
        allowed: true,
      },
      {
        id: "uninstall",
        name: "Uninstall Agents",
        icon: "🗑️",
        description: "Can remove agents from organization",
        allowed: true,
      },
      {
        id: "billing",
        name: "Manage Billing",
        icon: "💳",
        description: "Can manage subscriptions and payments",
        allowed: true,
      },
      {
        id: "admin",
        name: "Admin Operations",
        icon: "🔧",
        description: "Can perform administrative operations",
        allowed: true,
      },
      {
        id: "add_members",
        name: "Add Team Members",
        icon: "➕",
        description: "Can invite and add team members",
        allowed: true,
      },
      {
        id: "remove_members",
        name: "Remove Team Members",
        icon: "➖",
        description: "Can remove team members",
        allowed: true,
      },
      {
        id: "change_roles",
        name: "Change Member Roles",
        icon: "🔄",
        description: "Can change member roles",
        allowed: true,
      },
      {
        id: "transfer_ownership",
        name: "Transfer Ownership",
        icon: "↔️",
        description: "Can transfer organization ownership",
        allowed: true,
      },
      {
        id: "delete_org",
        name: "Delete Organization",
        icon: "🗑️",
        description: "Can delete the organization",
        allowed: true,
      },
      {
        id: "access_all_orgs",
        name: "Access All Organizations",
        icon: "🌐",
        description: "Can access all organizations on the platform",
        allowed: false,
      },
    ],
  },
  {
    id: "admin",
    name: "Admin",
    icon: "⚙️",
    color: "#50C878",
    level: 2,
    description: "Organization administrator with most permissions",
    capabilities: [
      "Install and configure agents",
      "Enable, disable, pause and resume agents",
      "Manage billing and subscriptions",
      "Add and remove team members",
      "Change member roles (promote Members to Admin, demote Admins to Member)",
      "View all agents and data",
    ],
    limitations: [
      "Cannot uninstall agents",
      "Cannot transfer organization ownership",
      "Cannot delete the organization",
      "Cannot invite other Admins (only Owners can)",
    ],
    permissions: [
      {
        id: "view",
        name: "View Agents",
        icon: "👁️",
        description: "Can see agents and data",
        allowed: true,
      },
      {
        id: "configure",
        name: "Configure Settings",
        icon: "⚙️",
        description: "Can change agent settings",
        allowed: true,
      },
      {
        id: "enable",
        name: "Enable Agents",
        icon: "▶️",
        description: "Can activate agents",
        allowed: true,
      },
      {
        id: "disable",
        name: "Disable Agents",
        icon: "⏸️",
        description: "Can deactivate agents",
        allowed: true,
      },
      {
        id: "pause",
        name: "Pause Agents",
        icon: "⏸️",
        description: "Can pause agent operations",
        allowed: true,
      },
      {
        id: "resume",
        name: "Resume Agents",
        icon: "▶️",
        description: "Can resume paused agents",
        allowed: true,
      },
      {
        id: "uninstall",
        name: "Uninstall Agents",
        icon: "🗑️",
        description: "Can remove agents from organization",
        allowed: false,
      },
      {
        id: "billing",
        name: "Manage Billing",
        icon: "💳",
        description: "Can manage subscriptions and payments",
        allowed: true,
      },
      {
        id: "admin",
        name: "Admin Operations",
        icon: "🔧",
        description: "Can perform administrative operations",
        allowed: false,
      },
      {
        id: "add_members",
        name: "Add Team Members",
        icon: "➕",
        description: "Can invite and add team members",
        allowed: true,
      },
      {
        id: "remove_members",
        name: "Remove Team Members",
        icon: "➖",
        description: "Can remove team members",
        allowed: true,
      },
      {
        id: "change_roles",
        name: "Change Member Roles",
        icon: "🔄",
        description: "Can change member roles",
        allowed: true,
      },
      {
        id: "transfer_ownership",
        name: "Transfer Ownership",
        icon: "↔️",
        description: "Can transfer organization ownership",
        allowed: false,
      },
      {
        id: "delete_org",
        name: "Delete Organization",
        icon: "🗑️",
        description: "Can delete the organization",
        allowed: false,
      },
      {
        id: "access_all_orgs",
        name: "Access All Organizations",
        icon: "🌐",
        description: "Can access all organizations on the platform",
        allowed: false,
      },
    ],
  },
  {
    id: "member",
    name: "Member",
    icon: "👤",
    color: "#95A5A6",
    level: 1,
    description: "Team member with view-only access",
    capabilities: [
      "View agents and their status",
      "View data and reports",
      "View organization information",
    ],
    limitations: [
      "Cannot install agents",
      "Cannot configure settings",
      "Cannot manage billing",
      "Cannot manage team members",
      "Cannot change any settings",
      "Cannot enable, disable, pause or resume agents",
    ],
    permissions: [
      {
        id: "view",
        name: "View Agents",
        icon: "👁️",
        description: "Can see agents and data",
        allowed: true,
      },
      {
        id: "configure",
        name: "Configure Settings",
        icon: "⚙️",
        description: "Can change agent settings",
        allowed: false,
      },
      {
        id: "enable",
        name: "Enable Agents",
        icon: "▶️",
        description: "Can activate agents",
        allowed: false,
      },
      {
        id: "disable",
        name: "Disable Agents",
        icon: "⏸️",
        description: "Can deactivate agents",
        allowed: false,
      },
      {
        id: "pause",
        name: "Pause Agents",
        icon: "⏸️",
        description: "Can pause agent operations",
        allowed: false,
      },
      {
        id: "resume",
        name: "Resume Agents",
        icon: "▶️",
        description: "Can resume paused agents",
        allowed: false,
      },
      {
        id: "uninstall",
        name: "Uninstall Agents",
        icon: "🗑️",
        description: "Can remove agents from organization",
        allowed: false,
      },
      {
        id: "billing",
        name: "Manage Billing",
        icon: "💳",
        description: "Can manage subscriptions and payments",
        allowed: false,
      },
      {
        id: "admin",
        name: "Admin Operations",
        icon: "🔧",
        description: "Can perform administrative operations",
        allowed: false,
      },
      {
        id: "add_members",
        name: "Add Team Members",
        icon: "➕",
        description: "Can invite and add team members",
        allowed: false,
      },
      {
        id: "remove_members",
        name: "Remove Team Members",
        icon: "➖",
        description: "Can remove team members",
        allowed: false,
      },
      {
        id: "change_roles",
        name: "Change Member Roles",
        icon: "🔄",
        description: "Can change member roles",
        allowed: false,
      },
      {
        id: "transfer_ownership",
        name: "Transfer Ownership",
        icon: "↔️",
        description: "Can transfer organization ownership",
        allowed: false,
      },
      {
        id: "delete_org",
        name: "Delete Organization",
        icon: "🗑️",
        description: "Can delete the organization",
        allowed: false,
      },
      {
        id: "access_all_orgs",
        name: "Access All Organizations",
        icon: "🌐",
        description: "Can access all organizations on the platform",
        allowed: false,
      },
    ],
  },
];

// ============================================
// PERMISSIONS MATRIX DATA
// ============================================

export const PERMISSIONS_MATRIX = {
  permissions: [
    { id: "view", name: "View Agents", icon: "👁️" },
    { id: "configure", name: "Configure Settings", icon: "⚙️" },
    { id: "enable", name: "Enable Agents", icon: "▶️" },
    { id: "disable", name: "Disable Agents", icon: "⏸️" },
    { id: "pause", name: "Pause Agents", icon: "⏸️" },
    { id: "resume", name: "Resume Agents", icon: "▶️" },
    { id: "uninstall", name: "Uninstall Agents", icon: "🗑️" },
    { id: "billing", name: "Manage Billing", icon: "💳" },
    { id: "admin", name: "Admin Operations", icon: "🔧" },
    { id: "add_members", name: "Add Team Members", icon: "➕" },
    { id: "remove_members", name: "Remove Team Members", icon: "➖" },
    { id: "change_roles", name: "Change Member Roles", icon: "🔄" },
    { id: "transfer_ownership", name: "Transfer Ownership", icon: "↔️" },
    { id: "delete_org", name: "Delete Organization", icon: "🗑️" },
    { id: "access_all_orgs", name: "Access All Organizations", icon: "🌐" },
  ],
  roles: ROLES.map((role) => ({
    id: role.id,
    name: role.name,
    permissions: role.permissions.reduce((acc, perm) => {
      acc[perm.id] = perm.allowed;
      return acc;
    }, {} as Record<string, boolean>),
  })),
};

// ============================================
// INVITATION RULES DATA
// ============================================

export const INVITATION_RULES: InvitationRule[] = [
  {
    inviterRole: "superadmin",
    canInvite: ["admin", "member"],
    cannotInvite: ["owner", "superadmin"],
    explanation:
      "Can invite Admins and Members. Cannot invite Owner (use Transfer Ownership) or SuperAdmin (platform-level role).",
  },
  {
    inviterRole: "owner",
    canInvite: ["admin", "member"],
    cannotInvite: ["owner", "superadmin"],
    explanation:
      "Can invite Admins and Members. Cannot invite Owner (use Transfer Ownership) or SuperAdmin (platform-level role).",
  },
  {
    inviterRole: "admin",
    canInvite: ["member"],
    cannotInvite: ["admin", "owner", "superadmin"],
    explanation:
      "Can only invite Members. Cannot invite Admins (only Owners can), Owner (use Transfer Ownership) or SuperAdmin (platform-level role).",
  },
  {
    inviterRole: "member",
    canInvite: [],
    cannotInvite: ["admin", "owner", "member", "superadmin"],
    explanation: "Members cannot invite anyone.",
  },
];

// ============================================
// USER JOURNEY DATA
// ============================================

export const USER_JOURNEY: JourneyStep[] = [
  {
    step: 1,
    title: "User Joins Platform",
    description: "User signs up for Forgeworks account",
    outcome: "Account created",
    role: "none",
  },
  {
    step: 2,
    title: "Create Organization",
    description: "User creates a new organization",
    outcome: "User becomes Owner",
    role: "owner",
  },
  {
    step: 3,
    title: "Or Get Invited",
    description: "User receives invitation to join organization",
    outcome: "User assigned role from invitation",
    role: "varies",
  },
  {
    step: 4,
    title: "Role Can Change",
    description: "Owner/Admin can promote or demote users",
    outcome: "User's role changes",
    role: "updated",
  },
  {
    step: 5,
    title: "Ownership Transfer",
    description: "Owner transfers ownership to another user",
    outcome: "New Owner, old Owner becomes Super Admin",
    role: "transferred",
  },
];

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get role by ID
 */
export const getRoleById = (roleId: string): Role | undefined => {
  return ROLES.find((role) => role.id === roleId);
};

/**
 * Get available roles for invitation based on current user's role
 */
export const getAvailableInviteRoles = (
  currentRole: "superadmin" | "owner" | "admin" | "member"
): Role[] => {
  const rule = INVITATION_RULES.find((r) => r.inviterRole === currentRole);
  if (!rule) return [];

  return ROLES.filter((role) => rule.canInvite.includes(role.id));
};

/**
 * Check if role can perform action
 * @param roleId - UserRole enum or role ID string
 * @param permissionId - Permission ID to check
 * @returns True if the role has the permission
 */
export const canPerformAction = (
  roleId: string | "superadmin" | "owner" | "admin" | "member",
  permissionId: string
): boolean => {
  // Normalize roleId to string if it's a UserRole enum
  const normalizedRoleId =
    typeof roleId === "string" ? roleId.toLowerCase() : roleId;
  const role = getRoleById(normalizedRoleId);
  if (!role) return false;

  const permission = role.permissions.find((p) => p.id === permissionId);
  return permission?.allowed || false;
};

/**
 * Get all permissions for a role
 */
export const getRolePermissions = (roleId: string): Permission[] => {
  const role = getRoleById(roleId);
  return role?.permissions || [];
};

/**
 * Compare two roles
 */
export const compareRoles = (
  roleId1: string,
  roleId2: string
): {
  role1: Role | undefined;
  role2: Role | undefined;
  differences: {
    permission: string;
    role1Has: boolean;
    role2Has: boolean;
  }[];
} => {
  const role1 = getRoleById(roleId1);
  const role2 = getRoleById(roleId2);

  if (!role1 || !role2) {
    return { role1, role2, differences: [] };
  }

  const allPermissionIds = new Set([
    ...role1.permissions.map((p) => p.id),
    ...role2.permissions.map((p) => p.id),
  ]);

  const differences = Array.from(allPermissionIds)
    .map((permId) => ({
      permission: permId,
      role1Has: canPerformAction(roleId1, permId),
      role2Has: canPerformAction(roleId2, permId),
    }))
    .filter((diff) => diff.role1Has !== diff.role2Has);

  return { role1, role2, differences };
};

// ============================================
// FAQ DATA
// ============================================

export const FAQ = [
  {
    question: "Can I have different roles in different organizations?",
    answer:
      "Yes! Roles are assigned per organization. You can be an Owner in one organization and a Member in another.",
  },
  {
    question: "What happens if I'm removed from an organization?",
    answer:
      "You'll lose access to that organization immediately. You can be re-invited if needed.",
  },
  {
    question: "Can I change my own role?",
    answer:
      "No, only Owners and Admins can change roles. You cannot change your own role.",
  },
  {
    question: "What's the difference between Owner and Super Admin?",
    answer:
      "Owner has full control of their specific organization. Super Admin has platform-level access to all organizations.",
  },
  {
    question: "Can a Member become an Admin?",
    answer:
      "Yes, if an Owner or Admin promotes them. Members cannot promote themselves.",
  },
  {
    question: "What happens when ownership is transferred?",
    answer:
      "The new user becomes Owner with full control. The old Owner automatically becomes a Super Admin and retains access to the organization.",
  },
  {
    question: "Can I invite someone as Owner?",
    answer:
      "No, you cannot invite someone as Owner. Use the 'Transfer Ownership' feature instead.",
  },
  {
    question: "Why can't Admins invite other Admins?",
    answer:
      "This maintains proper role hierarchy. Only Owners should be able to create Admins.",
  },
  {
    question: "How do I know what role I have?",
    answer:
      "Your role is displayed in your profile and organization settings. You'll also see role-based restrictions in the UI.",
  },
  {
    question: "What if I need more permissions?",
    answer:
      "Contact your organization Owner or Admin to request a role change.",
  },
];

// ============================================
// CONTENT FOR UI
// ============================================

export const PAGE_CONTENT = {
  hero: {
    title: "Roles & Permissions",
    subtitle: "Understand how roles work in your organization",
    description:
      "Forgeworks uses a role-based access control system to manage permissions across your organization. Each user has a role that determines what they can do in the platform. Roles are assigned at the organization level, so a user can have different roles in different organizations.",
    keyPoints: [
      "Roles control what users can do in the platform",
      "Roles are assigned per organization",
      "Four role levels: Super Admin, Owner, Admin and Member",
      "Organization owners can manage roles and permissions",
    ],
  },
  sections: {
    hierarchy: {
      title: "Role Hierarchy",
      description:
        "Roles are organized in a hierarchy from highest to lowest permissions",
    },
    details: {
      title: "Role Details",
      description: "Learn what each role can do",
    },
    permissions: {
      title: "Permissions Matrix",
      description: "Quick reference for what each role can do",
    },
    invitations: {
      title: "Inviting Team Members",
      description: "Who can invite whom and what roles can be assigned",
    },
    journey: {
      title: "How Roles Work",
      description: "Understand how roles are assigned and changed",
    },
    faq: {
      title: "Frequently Asked Questions",
      description: "Common questions about roles and permissions",
    },
  },
};
